/**
 * Answer feedback: short synthesized sounds (WebAudio, no audio files) and a vibration
 * where the device supports it. One per-device switch in Профиль turns both off.
 */
import { useSyncExternalStore } from 'react';
import { readString, writeString } from '../data/storage.ts';

const STORAGE_KEY = 'soile.feedback';
const listeners = new Set<() => void>();

export function feedbackEnabled(): boolean {
  return readString(STORAGE_KEY) !== 'off';
}

export function setFeedbackEnabled(on: boolean): void {
  writeString(STORAGE_KEY, on ? null : 'off');
  listeners.forEach((l) => l());
}

export function useFeedbackEnabled(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    feedbackEnabled,
    () => true,
  );
}

type Note = { freq: number; at: number; length: number; type?: OscillatorType; gain?: number };

const SOUNDS: Record<
  'correct' | 'wrong' | 'complete' | 'poke' | 'wake' | 'grumble',
  { notes: Note[]; vibrate: number | number[] }
> = {
  // Robo-cat: a tiny blip when touched.
  poke: {
    notes: [{ freq: 1318.5, at: 0, length: 0.06, type: 'triangle', gain: 0.08 }],
    vibrate: 10,
  },
  // Robo-cat waking up: a quick rising chirp.
  wake: {
    notes: [
      { freq: 784, at: 0, length: 0.07, type: 'triangle', gain: 0.09 },
      { freq: 1175, at: 0.06, length: 0.1, type: 'triangle', gain: 0.09 },
    ],
    vibrate: [15, 30, 15],
  },
  // Robo-cat getting angry: a short low growl.
  grumble: {
    notes: [
      { freq: 130.8, at: 0, length: 0.12, type: 'sawtooth', gain: 0.05 },
      { freq: 110, at: 0.1, length: 0.16, type: 'sawtooth', gain: 0.05 },
    ],
    vibrate: [30, 40, 30],
  },
  // A bright rising fifth.
  correct: {
    notes: [
      { freq: 659.25, at: 0, length: 0.11 },
      { freq: 987.77, at: 0.09, length: 0.18 },
    ],
    vibrate: 25,
  },
  // A soft low double buzz.
  wrong: {
    notes: [
      { freq: 196, at: 0, length: 0.14, type: 'square', gain: 0.07 },
      { freq: 174.61, at: 0.15, length: 0.2, type: 'square', gain: 0.07 },
    ],
    vibrate: [40, 60, 40],
  },
  // A major arpeggio up to the octave.
  complete: {
    notes: [
      { freq: 523.25, at: 0, length: 0.14 },
      { freq: 659.25, at: 0.12, length: 0.14 },
      { freq: 783.99, at: 0.24, length: 0.14 },
      { freq: 1046.5, at: 0.36, length: 0.32 },
    ],
    vibrate: [30, 50, 30, 50, 60],
  },
};

let context: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null;
  context ??= new AudioContext();
  // iOS starts contexts suspended until a user gesture; play() is always called from one.
  if (context.state === 'suspended') void context.resume();
  return context;
}

export function playFeedback(kind: keyof typeof SOUNDS): void {
  if (!feedbackEnabled()) return;
  const sound = SOUNDS[kind];
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(sound.vibrate);
  const ctx = audio();
  if (!ctx) return;
  const start = ctx.currentTime + 0.01;
  for (const note of sound.notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = note.type ?? 'sine';
    osc.frequency.value = note.freq;
    const t = start + note.at;
    const peak = note.gain ?? 0.16;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + note.length);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + note.length + 0.02);
  }
}
